import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Video, Plus, X, Save, Loader2, Calendar, ExternalLink, Trash2,
  Radio, Clock, PlayCircle, Edit3, Copy, Check, MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const PLATFORMS = [
  { id: "youtube",    label: "YouTube Live" },
  { id: "facebook",   label: "Facebook Live" },
  { id: "zoom",       label: "Zoom" },
  { id: "streamyard", label: "StreamYard" },
  { id: "vimeo",      label: "Vimeo" },
  { id: "tiktok",     label: "TikTok Live" },
  { id: "twitch",     label: "Twitch" },
  { id: "kick",       label: "Kick" },
  { id: "linkedin",   label: "LinkedIn Live" },
  { id: "rumble",     label: "Rumble" },
  { id: "amazon",     label: "Amazon Live" },
  { id: "whatnot",    label: "Whatnot" },
  { id: "instagram",  label: "Instagram Live" },
  { id: "custom",     label: "Custom link / embed" },
];

const STATUSES = [
  { id: "draft",     label: "Draft",            color: "bg-white/10 text-[#94A3B8]" },
  { id: "scheduled", label: "Scheduled",        color: "bg-[#3B82F6]/15 text-[#3B82F6]" },
  { id: "live",      label: "Live Now",         color: "bg-[#EF4444]/15 text-[#EF4444] animate-pulse" },
  { id: "replay",    label: "Replay Available", color: "bg-[#10B981]/15 text-[#10B981]" },
  { id: "ended",     label: "Ended",            color: "bg-white/5 text-[#64748B]" },
];

const blank = {
  listing_slug: "",
  title: "",
  description: "",
  image_url: "",
  starts_at: "",
  ends_at: "",
  platform: "youtube",
  stream_url: "",
  embed_code: "",
  cta_label: "",
  cta_url: "",
  offer_text: "",
  offer_code: "",
  offer_url: "",
  lead_form_enabled: false,
  question_form_enabled: false,
  replay_url: "",
  status: "draft",
};

export default function LiveEventsPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ events: [], listings: [] });
  const [editing, setEditing] = useState(null); // null | "new" | event_id
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [questionsFor, setQuestionsFor] = useState(null); // event_id

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/live-events`, { headers: getAuthHeader() });
      setData(r.data);
    } catch (_) {
      setData({ events: [], listings: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const openNew = () => {
    setEditing("new");
    setForm({ ...blank, listing_slug: data.listings?.[0]?.slug || "" });
  };

  const openEdit = (ev) => {
    setEditing(ev.id);
    setForm({ ...blank, ...ev, starts_at: (ev.starts_at || "").slice(0, 16), ends_at: (ev.ends_at || "").slice(0, 16) });
  };

  const close = () => { setEditing(null); setForm(blank); };

  const save = async () => {
    if (!form.title || !form.listing_slug) {
      toast.error("Title and listing are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      if (editing === "new") {
        await axios.post(`${API}/dashboard/live-events`, payload, { headers: getAuthHeader() });
        toast.success("Live event created");
      } else {
        // remove unchangeable fields
        const { listing_slug, ...patch } = payload; // eslint-disable-line
        await axios.patch(`${API}/dashboard/live-events/${editing}`, patch, { headers: getAuthHeader() });
        toast.success("Live event updated");
      }
      close();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this live event?")) return;
    try {
      await axios.delete(`${API}/dashboard/live-events/${id}`, { headers: getAuthHeader() });
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="live-events-loading">
        <div className="h-24 bg-white/5 rounded-2xl animate-pulse" />
        <div className="h-48 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!data.listings?.length) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="live-events-no-listing">
        <Video className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
        <p className="text-white font-semibold">Create or claim your listing first.</p>
        <p className="text-[#94A3B8] text-sm mt-1">Live Events live inside your listing's public page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="live-events-page">
      <header className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#EF4444]/10 via-[#0F172A] to-[#3B82F6]/10 p-5 sm:p-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[#EF4444]">Live</p>
          <h1 className="text-xl sm:text-2xl font-bold text-white font-['Outfit']">Live Events & Replays</h1>
          <p className="text-sm text-[#94A3B8] mt-1">Promote live shows, demos, Q&As, open houses, flash sales, fundraisers, and replays.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#EF4444] to-[#EC4899] text-white text-sm font-semibold hover:opacity-90 flex-shrink-0" data-testid="live-events-new-btn">
          <Plus className="w-4 h-4" /> New Live Event
        </button>
      </header>

      {data.events.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="live-events-empty">
          <Radio className="w-10 h-10 text-[#EF4444] mx-auto mb-3" />
          <p className="text-white font-medium">No live events yet.</p>
          <p className="text-[#94A3B8] text-sm mt-1">Create your first one — it'll appear on your public listing instantly.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.events.map((ev) => <EventRow key={ev.id} ev={ev} onEdit={() => openEdit(ev)} onDelete={() => remove(ev.id)} onShowQuestions={() => setQuestionsFor(ev.id)} />)}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <EventEditor
          form={form}
          setForm={setForm}
          listings={data.listings}
          isNew={editing === "new"}
          saving={saving}
          onSave={save}
          onClose={close}
        />
      )}

      {/* Questions modal */}
      {questionsFor && (
        <QuestionsViewer eventId={questionsFor} onClose={() => setQuestionsFor(null)} getAuthHeader={getAuthHeader} />
      )}
    </div>
  );
}

function EventRow({ ev, onEdit, onDelete, onShowQuestions }) {
  const status = STATUSES.find((s) => s.id === ev.status) || STATUSES[0];
  const eventUrl = `${window.location.origin}/live/${ev.listing_slug}/${ev.event_slug}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(eventUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5"
      data-testid={`live-event-row-${ev.event_slug}`}
    >
      <div className="flex items-start gap-4 flex-wrap">
        {ev.image_url ? (
          <img src={ev.image_url} alt={ev.title} className="w-20 h-20 rounded-xl object-cover bg-white/10 flex-shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Video className="w-7 h-7 text-[#94A3B8]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${status.color}`}>
              {ev.status === "live" && <Radio className="w-2.5 h-2.5" />} {status.label}
            </span>
            <span className="text-[10px] text-[#64748B] uppercase tracking-wider">{ev.platform}</span>
            {ev.starts_at && (
              <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {new Date(ev.starts_at).toLocaleString()}
              </span>
            )}
          </div>
          <p className="text-white font-semibold truncate">{ev.title}</p>
          <p className="text-xs text-[#94A3B8] truncate">{ev.listing_name || ev.listing_slug}</p>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-[#64748B] flex-wrap">
            <span>{ev.views || 0} views</span>
            <span>·</span>
            <span>{ev.registrations || 0} regs</span>
            <span>·</span>
            <span>{ev.questions || 0} Qs</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={eventUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white text-xs" data-testid={`live-event-view-${ev.event_slug}`}>
            <ExternalLink className="w-3 h-3" /> View
          </a>
          <button onClick={copy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white text-xs">
            {copied ? <Check className="w-3 h-3 text-[#10B981]" /> : <Copy className="w-3 h-3" />} Link
          </button>
          {ev.question_form_enabled && (
            <button onClick={onShowQuestions} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white text-xs">
              <MessageSquare className="w-3 h-3" /> Qs
            </button>
          )}
          <button onClick={onEdit} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/30 text-[#A78BFA] text-xs" data-testid={`live-event-edit-${ev.event_slug}`}>
            <Edit3 className="w-3 h-3" /> Edit
          </button>
          <button onClick={onDelete} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[#EF4444] hover:bg-[#EF4444]/10 text-xs" data-testid={`live-event-delete-${ev.event_slug}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function EventEditor({ form, setForm, listings, isNew, saving, onSave, onClose }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="live-event-editor" onClick={onClose}>
      <div className="bg-[#0F172A] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0F172A] border-b border-white/5 px-5 py-4 flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">{isNew ? "New Live Event" : "Edit Live Event"}</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {isNew && (
            <Field label="Listing">
              <select value={form.listing_slug} onChange={(e) => set("listing_slug", e.target.value)} className={inputCls} data-testid="event-listing-select">
                {listings.map((l) => <option key={l.slug} value={l.slug} className="bg-[#0F172A]">{l.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Event title">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="e.g. Flash sale demo + Q&A" data-testid="event-title-input" />
          </Field>
          <Field label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} placeholder="What's the event about? Why should people watch?" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Starts at">
              <input type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} className={inputCls} data-testid="event-starts-input" />
            </Field>
            <Field label="Ends at">
              <input type="datetime-local" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Thumbnail / featured image URL">
            <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className={inputCls} placeholder="https://..." />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Platform">
              <select value={form.platform} onChange={(e) => set("platform", e.target.value)} className={inputCls} data-testid="event-platform-select">
                {PLATFORMS.map((p) => <option key={p.id} value={p.id} className="bg-[#0F172A]">{p.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="event-status-select">
                {STATUSES.map((s) => <option key={s.id} value={s.id} className="bg-[#0F172A]">{s.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Live stream URL">
            <input value={form.stream_url} onChange={(e) => set("stream_url", e.target.value)} className={inputCls} placeholder="https://youtube.com/watch?v=..." data-testid="event-stream-url-input" />
          </Field>
          <Field label="Optional embed code (iframe / script)">
            <textarea rows={3} value={form.embed_code} onChange={(e) => set("embed_code", e.target.value)} className={`${inputCls} font-mono text-xs`} placeholder="<iframe src='...' ...></iframe>" />
          </Field>
          <Field label="Replay URL (for after the event)">
            <input value={form.replay_url} onChange={(e) => set("replay_url", e.target.value)} className={inputCls} placeholder="https://..." />
          </Field>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#FBBF24]">CTA button</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="CTA label">
                <input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} className={inputCls} placeholder="Claim Offer" />
              </Field>
              <Field label="CTA URL">
                <input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} className={inputCls} placeholder="https://..." />
              </Field>
            </div>
          </div>

          <div className="rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 p-4 space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#10B981]">Offer / reward (optional)</p>
            <Field label="Offer text">
              <input value={form.offer_text} onChange={(e) => set("offer_text", e.target.value)} className={inputCls} placeholder="10% off live-only" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Promo code">
                <input value={form.offer_code} onChange={(e) => set("offer_code", e.target.value)} className={inputCls} placeholder="LIVE10" />
              </Field>
              <Field label="Redeem URL">
                <input value={form.offer_url} onChange={(e) => set("offer_url", e.target.value)} className={inputCls} placeholder="https://..." />
              </Field>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Toggle label="Enable lead capture form (Get Reminder)" checked={form.lead_form_enabled} onChange={(v) => set("lead_form_enabled", v)} testid="event-lead-toggle" />
            <Toggle label="Enable Q&A form" checked={form.question_form_enabled} onChange={(v) => set("question_form_enabled", v)} testid="event-question-toggle" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-[#0F172A] border-t border-white/5 px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#94A3B8] hover:text-white">Cancel</button>
          <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#EF4444] to-[#EC4899] text-white text-sm font-semibold disabled:opacity-60" data-testid="event-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save event
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionsViewer({ eventId, onClose, getAuthHeader }) {
  const [qs, setQs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dashboard/live-events/${eventId}/questions`, { headers: getAuthHeader() })
      .then((r) => setQs(r.data.questions || []))
      .catch(() => setQs([]));
  }, [eventId, getAuthHeader]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0F172A] border border-white/10 rounded-2xl max-w-xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="event-questions-viewer">
        <div className="sticky top-0 bg-[#0F172A] border-b border-white/5 px-5 py-3 flex items-center justify-between">
          <h2 className="text-white font-semibold">Audience questions</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {qs === null ? <p className="text-[#94A3B8] text-sm">Loading…</p> : qs.length === 0 ? <p className="text-[#94A3B8] text-sm text-center py-6">No questions yet.</p> : qs.map((q) => (
            <div key={q.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-white text-sm">{q.question}</p>
              <p className="text-xs text-[#64748B] mt-1.5">— {q.name || "Anonymous"}{q.email ? ` · ${q.email}` : ""} · {new Date(q.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 bg-[#070B16] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#94A3B8] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={testid}>
      <span className="relative inline-block w-11 h-6 flex-shrink-0">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer opacity-0 absolute inset-0 cursor-pointer" />
        <span className="block w-11 h-6 rounded-full bg-white/15 peer-checked:bg-[#FBBF24] transition-colors" />
        <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
      </span>
      <span className="text-sm text-white">{label}</span>
    </label>
  );
}
